import { Navigate, useRoutes, useLocation } from "react-router-dom";
import { useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import RegisterUserPage from "./pages/login/RegisterUserPage";
import VanityLogin from "./pages/vanityLogin/VanityLogin";
import StartingPage from "./pages/startingPage/StartingPage";
import VanityStartingPage from "./pages/vanityLogin/VanityStartingPage";
import Login from "./pages/login/Login";
import GuestLogin from "./pages/login/GuestLogin";
import Console from "./pages/console/Console";
import { Logout } from "./api/UserController";
import Profile from "./pages/console/user/profile/Profile";
import Wallet from "./pages/console/user/wallet/Wallet";
import Orders from "./pages/console/user/orders/Orders";
import Rooms from "./pages/console/admin/rooms/Rooms";
import Achievements from "./pages/console/admin/achievements/Achievements";
import RoomsDetails from "./pages/console/admin/rooms/components/RoomsDetails";
import RoomSettings from "./pages/console/admin/rooms/components/RoomSettings";
import RoomRelations from "./pages/console/admin/rooms/components/RoomRelations";
import RoomUsers from "./pages/console/admin/rooms/components/RoomUsers";
import RoomArcadeGames from "./pages/console/admin/rooms/components/RoomArcadeGames";
import RoomCasinoGames from "./pages/console/admin/rooms/components/RoomCasinoGames";
import {
    fetchUserId,
    fetchUserName,
    fetchUserRole,
    fetchUserToken,
    fetchIsUserOwnerOfClient,
} from "./redux/slices/users";
import { SUPERADMIN_ROLE, ADMIN_ROLE, USER_ROLE, GUEST_ROLE } from "./constants";
import NotFound from "./pages/Page404";
import Users from "./pages/console/admin/users/Users";
import VanityLinks from "./pages/console/admin/vanityLinks/VanityLinks";
import VanityLinksCreate from "./pages/console/admin/vanityLinks/components/CreateLink";
import GuestCheckout from "./pages/guestCheckout/GuestCheckout";
import Checkout from "./pages/guestCheckout/components/checkout";
import CheckoutSuccess from "./pages/guestCheckout/components/success";
import CheckoutCancel from "./pages/guestCheckout/components/cancel";
import Tables from "./pages/console/admin/tables/Tables";
import DynamicTable from "./pages/console/admin/tables/DynamicTable";
import RecordDetails from "./pages/console/admin/record/RecordDetails";
import ForgotPassword from "./pages/login/ForgotPassword";
import UpdatePassword from "./pages/login/UpdatePassword";
import Merchants from "./pages/console/admin/merchant/Merchants";
import MerchantsDetails from "./pages/console/admin/merchant/components/MerchantsDetails";
import DynamicTableFiles from "./pages/console/admin/files/DynamicTableFiles";
import Surveys from "./pages/console/admin/surveys/Surveys";
import SurveysQuestions from "./pages/console/admin/surveys/components/SurveysQuestionsTable";
import QuestionOptions from "./pages/console/admin/surveys/components/QuestionOptionsTable";
import NPCDialogues from "./pages/console/admin/npc_dialogues/NPCDialogues";
import ImageEditor from "./pages/console/admin/image_editor/ImageEditor";
import CustomizationPage from "./pages/console/user/customization/CustomizationPage";
import Roles from "./pages/console/admin/rolesPermissions/Roles";
import Permissions from "./pages/console/admin/rolesPermissions/Permissions";
import WrapperWithCookieAuth from "./hooks/wrappers/WrapperWithCookieAuth";
import WrapperCheckUserRoomPermission from "./hooks/wrappers/WrapperCheckUserRoomPermission";
import Live from "./pages/landingPages/live/Live";
import { RoomSettingsProvider } from "./pages/render-stream/RoomSettingsContext";
import Pro from "./pages/landingPages/pro/Pro";
import GuestBookPage from "./pages/console/user/guestBook/GuestBook";
import EventsEmailForm from "./pages/landingPages/clientEvents/EventsEmailForm";
import PasswordProtectMain from "./pages/landingPages/components/PasswordProtectMain";
import RoomInvitePage from "./pages/console/user/roomInvites/RoomInvitePage";
import UserGuestBookPage from "./pages/console/user/userGuestBook/UserGuestBook";
import VagonStreamIntraverse from "./features/vagon/VagonStreamIntraverse";
import VanityURLRedirects from "./features/vagon/VanityURLRedirects";
import Analytics from "./pages/console/admin/analytics/Analytics";
import ClientTag from "./pages/console/admin/client_tag/ClientTag";
import { store } from "./redux/Store";
import CreateClientTag from "./pages/console/admin/client_tag/components/CreateClientTag";
import RoomPreset from "./pages/console/admin/room_preset/RoomPreset";
import { setToSessionStorage } from "./hooks/useSessionStorage";
import MyRooms from "./pages/console/user/myRooms/MyRooms";
import SidebarConsole from "./pages/console/user/myRooms/SidebarConsole";
import VerifyEmail from "./pages/guestConversion/VerifyEmail";
import CreateUser from "./pages/guestConversion/CreateUser";
import QuestBuilder from "./pages/console/admin/quest_builder/QuestBuilder";
import QuestsDashboard from "./pages/console/admin/quest_builder/QuestsDashboard";

// Check for /netcoins in URL and set localStorage
function checkNetcoinsInUrl() {
    if (window.location.pathname.includes("/netcoins")) {
        setToSessionStorage("netcoins", "true");
    } else {
        setToSessionStorage("netcoins", "false");
    }
}
// Run on initial load
checkNetcoinsInUrl();

window.addEventListener("storage", (e) => {
    if (e.key === "persist:user") {
        try {
            const json = JSON.parse(e.newValue)?.user;
            if (json && !JSON.parse(json).user) {
                store.dispatch(Logout());
            }
        } catch (err) {
            console.error("Storage event parsing error:", err);
        }
    }
});

const DynamicLoginComponent = () => {
    const location = useLocation();
    const path = location.pathname;

    if (path.includes("iframe")) {
        return null;
    }

    // Comment or remove below to change old vanity pages to new vanity flow
    if (path === "/teddyswims" || path === "/discoverse" || path.includes("/discoverse")) {
        return <VanityLogin />;
    }
    return <VanityStartingPage />;
};

const DynamicPasswordComponent = ({ path }) => {
    // to remove password protection on prod set the below boolean to true
    const [allow, setAllow] = useState(true);
    // If not in production, show the login or registration page directly
    if (process.env.REACT_APP_STREAM_API_ENV !== "production") {
        if (path === "register") {
            return <RegisterUserPage />;
        }
        if (path === "login") {
            return <Login style={{ zIndex: 1000 }} />;
        }
    }
    // In production, show the password protection first, then grant access
    // eslint-disable-next-line no-nested-ternary
    return allow ? (
        path === "register" ? (
            <RegisterUserPage />
        ) : (
            <Login style={{ zIndex: 1000 }} />
        )
    ) : (
        <PasswordProtectMain setAllow={setAllow} />
    );
};

export default function Router() {
    const user = useSelector(fetchUserName);
    const role = useSelector(fetchUserRole);
    const token = useSelector(fetchUserToken);
    const userId = useSelector(fetchUserId);
    const userState = useSelector((state) => state.user);
    const isUserOwnerOfClient = useSelector(fetchIsUserOwnerOfClient);

    return useRoutes([
        {
            path: "reset-password",
            element: (
                <RedirectIfLoggedIn user={user} token={token} userId={userId} role={role}>
                    {" "}
                    <ForgotPassword />
                </RedirectIfLoggedIn>
            ),
        },
        {
            path: "reset-password/:token",
            element: <UpdatePassword />,
        },
        {
            path: ":roomName/:productId/guest-checkout",
            element: (
                <UserConsoleRoute user={user} token={token} userId={userId} role={role}>
                    <GuestCheckout />
                </UserConsoleRoute>
            ),
            children: [
                {
                    path: "",
                    element: <Checkout />,
                },
                {
                    path: "success",
                    element: <CheckoutSuccess />,
                },
                {
                    path: "cancel",
                    element: <CheckoutCancel />,
                },
            ],
        },
        {
            path: "console",
            element: (
                <UserConsoleRoute user={user} token={token} userId={userId} role={role}>
                    <Console />
                </UserConsoleRoute>
            ),
            children: [
                {
                    path: "profile",
                    element: <Profile />,
                },
                {
                    path: "orders",
                    element: <Orders />,
                },
                {
                    path: "wallet",
                    element: <Wallet />,
                },
                {
                    path: ":userId/my-rooms",
                    element: <MyRooms />,
                },
                {
                    path: ":userId/my-rooms/:roomId",
                    element: <SidebarConsole />,
                },
                {
                    path: "analytics",
                    element: (
                        <IsUserOwnerOfClient isAccess={isUserOwnerOfClient} role={role}>
                            <Analytics />
                        </IsUserOwnerOfClient>
                    ),
                },
                {
                    path: "users",
                    element: (
                        <RedirectIfNotAdmin role={role}>
                            <Users />
                        </RedirectIfNotAdmin>
                    ),
                },
                {
                    path: "quest-dashboard",
                    element: (
                        <RedirectIfNotAdmin role={role}>
                            <QuestsDashboard />
                        </RedirectIfNotAdmin>
                    ),
                },
                {
                    path: "quest-builder",
                    element: (
                        <RedirectIfNotAdmin role={role}>
                            <QuestBuilder />
                        </RedirectIfNotAdmin>
                    ),
                },
                {
                    path: "quest-builder/:questId",
                    element: (
                        <RedirectIfNotAdmin role={role}>
                            <QuestBuilder />
                        </RedirectIfNotAdmin>
                    ),
                },
                {
                    path: "user-guestbook",
                    element: <UserGuestBookPage />,
                },
                {
                    path: "rooms",
                    element: (
                        <RedirectIfNotAdmin role={role}>
                            <Rooms />
                        </RedirectIfNotAdmin>
                    ),
                },
                {
                    path: "achievements",
                    element: (
                        <RedirectIfNotAdmin role={role}>
                            <Achievements />
                        </RedirectIfNotAdmin>
                    ),
                },
                {
                    path: "rooms/:id",
                    element: <RoomsDetails />,
                },
                {
                    path: "rooms/:id/settings",
                    element: <RoomSettings />,
                },
                {
                    path: "rooms/:id/guest-book",
                    element: <GuestBookPage />,
                },
                {
                    path: "rooms/:id/images",
                    element: <CustomizationPage token={token} userID={userId} />,
                },
                {
                    path: "rooms/:id/casino-games",
                    element: (
                        <RedirectIfNotAdmin role={role}>
                            <RoomCasinoGames />
                        </RedirectIfNotAdmin>
                    ),
                },
                {
                    path: "rooms/:id/arcade-games",
                    element: (
                        <RedirectIfNotAdmin role={role}>
                            <RoomArcadeGames />
                        </RedirectIfNotAdmin>
                    ),
                },
                {
                    path: "rooms/:id/subrooms",
                    element: (
                        <RedirectIfNotAdmin role={role}>
                            <RoomRelations />
                        </RedirectIfNotAdmin>
                    ),
                },
                {
                    path: "rooms/:id/users",
                    element: (
                        <RedirectIfNotAdmin role={role}>
                            <RoomUsers />
                        </RedirectIfNotAdmin>
                    ),
                },
                {
                    path: "record_details",
                    element: (
                        <RedirectIfNotAdmin role={role}>
                            <RecordDetails />
                        </RedirectIfNotAdmin>
                    ),
                },
                {
                    path: "vanity-links",
                    element: (
                        <RedirectIfNotAdmin role={role}>
                            <VanityLinks />
                        </RedirectIfNotAdmin>
                    ),
                },
                {
                    path: "vanity-links/create",
                    element: (
                        <RedirectIfNotAdmin role={role}>
                            <VanityLinksCreate />
                        </RedirectIfNotAdmin>
                    ),
                },
                {
                    path: "tables",
                    element: (
                        <RedirectIfNotAdmin role={role}>
                            <Tables />
                        </RedirectIfNotAdmin>
                    ),
                },
                {
                    path: "files_dashboard",
                    element: (
                        <RedirectIfNotAdmin role={role}>
                            <DynamicTableFiles />
                        </RedirectIfNotAdmin>
                    ),
                },
                {
                    path: "table/:tableName",
                    element: (
                        <RedirectIfNotAdmin role={role}>
                            <DynamicTable />
                        </RedirectIfNotAdmin>
                    ),
                },
                {
                    path: "merchants",
                    element: (
                        <RedirectIfNotAdmin role={role}>
                            <Merchants />
                        </RedirectIfNotAdmin>
                    ),
                },
                {
                    path: "merchants/:id",
                    element: (
                        <RedirectIfNotAdmin role={role}>
                            <MerchantsDetails />
                        </RedirectIfNotAdmin>
                    ),
                },
                {
                    path: "surveys",
                    element: (
                        <RedirectIfNotAdmin role={role}>
                            <Surveys />
                        </RedirectIfNotAdmin>
                    ),
                },
                {
                    path: "surveys/:surveyId/questions",
                    element: (
                        <RedirectIfNotAdmin role={role}>
                            <SurveysQuestions />
                        </RedirectIfNotAdmin>
                    ),
                },
                {
                    path: "surveys/:surveyId/questions/:questionId/options",
                    element: (
                        <RedirectIfNotAdmin role={role}>
                            <QuestionOptions />
                        </RedirectIfNotAdmin>
                    ),
                },
                {
                    path: "npc_dialogues/:roomId",
                    element: (
                        <RedirectIfNotAdmin role={role}>
                            <NPCDialogues />
                        </RedirectIfNotAdmin>
                    ),
                },

                {
                    path: "image_editor",
                    element: (
                        <RedirectIfNotAdmin role={role}>
                            <ImageEditor />
                        </RedirectIfNotAdmin>
                    ),
                },
                {
                    path: "roles",
                    element: (
                        <RedirectIfNotAdmin role={role}>
                            <Roles />
                        </RedirectIfNotAdmin>
                    ),
                },
                {
                    path: ":roleId/permissions",
                    element: (
                        <RedirectIfNotAdmin role={role}>
                            <Permissions />
                        </RedirectIfNotAdmin>
                    ),
                },
                {
                    path: "client_tag",
                    element: (
                        <RedirectIfNotAdmin role={role}>
                            <ClientTag />
                        </RedirectIfNotAdmin>
                    ),
                },
                {
                    path: "client_tag/create",
                    element: (
                        <RedirectIfNotAdmin role={role}>
                            <CreateClientTag />
                        </RedirectIfNotAdmin>
                    ),
                },
                {
                    path: "room_preset",
                    element: (
                        <RedirectIfNotAdmin role={role}>
                            <RoomPreset />
                        </RedirectIfNotAdmin>
                    ),
                },
            ],
        },
        {
            path: "verify-email/:paramUserId?/:paramToken?",
            element: (
                <RedirectIfIsUser token={token} userId={userId}>
                    <VerifyEmail />
                </RedirectIfIsUser>
            ),
        },
        {
            path: "/room_personalization",
            element: (
                <CustomizationPage token={token} />
                // <WrapperWithCookieAuth>
                //     <WrapperCheckUserRoomPermission>
                //         {({ token }) => <CustomizationPage token={token} />}
                //     </WrapperCheckUserRoomPermission>
                // </WrapperWithCookieAuth>
            ),
        },
        {
            path: "/event/:id",
            element: <EventsEmailForm />,
        },
        {
            path: "autologin",
            element: <VanityStartingPage autoLogin />,
        },
        {
            path: "login",
            element: (
                <>
                    <RedirectIfLoggedIn user={user} token={token} userId={userId} role={role}>
                        <DynamicPasswordComponent path="login" />
                    </RedirectIfLoggedIn>
                </>
            ),
        },
        {
            path: "pro",
            element: <Pro />,
        },
        {
            path: "live",
            element: <Live />,
        },
        {
            path: "register",
            element: (
                <>
                    <RedirectIfLoggedIn user={user}>
                        <DynamicPasswordComponent path="register" />
                    </RedirectIfLoggedIn>
                </>
            ),
        },
        {
            path: "sign-up",
            element: <CreateUser />,
        },
        {
            path: "404",
            element: <NotFound />,
        },
        {
            path: "/guest/login/:roomId?",
            element: <GuestLogin />,
        },
        {
            path: "*",
            element: <DynamicLoginComponent />,
            children: [
                {
                    path: "",
                    element: null,
                },
            ],
        },
        {
            path: "/:roomId/invite/:roomInviteLinkId",
            element: <RoomInvitePage />,
        },
        {
            path: "/",
            element: !user ? <Live /> : <StartingPage />,
            children: [
                { path: "/", element: <Navigate to="/dashboard/app" /> },
                { path: "404", element: <NotFound /> },
                { path: "*", element: user ? <Navigate to="/404" /> : <Navigate to="/login" /> },
            ],
        },
    ]);
}

function ProtectedRoute({ children, user, token, userId, role }) {
    if (
        (user === "" && token === "" && userId === "" && role === "") ||
        role !== SUPERADMIN_ROLE ||
        role !== ADMIN_ROLE
    ) {
        return <Navigate to="/" replace />;
    }
    return children;
}
ProtectedRoute.propTypes = {
    children: PropTypes.any,
    user: PropTypes.any,
    token: PropTypes.any,
    userId: PropTypes.any,
    role: PropTypes.any,
};
function UserConsoleRoute({ children, user, token, userId, role }) {
    if (user === "" && token === "" && userId === "" && role === "") {
        return <Navigate to="/" replace />;
    }
    return children;
}
UserConsoleRoute.propTypes = {
    children: PropTypes.any,
    user: PropTypes.any,
    token: PropTypes.any,
    userId: PropTypes.any,
    role: PropTypes.any,
};

function RedirectIfLoggedIn({ children, user, token, userId }) {
    if (user !== "" && token !== "" && userId !== "") {
        // if (role === SUPERADMIN_ROLE) {
        //   return <Navigate to="/call-to-action" replace />;
        // }
        return <Navigate to="/" replace />;
    }

    return children;
}

RedirectIfLoggedIn.propTypes = {
    children: PropTypes.any,
    user: PropTypes.any,
    token: PropTypes.any,
    userId: PropTypes.any,
};

function RedirectIfNotAdmin({ role, children }) {
    if (role !== SUPERADMIN_ROLE && role !== ADMIN_ROLE) {
        return <Navigate to="/console/profile" replace />;
    }
    return children;
}

function RedirectIfIsUser({ token, userId, children }) {
    if (userId === "" && token === "") {
        return <Navigate to="/" replace />;
    }
    return children;
}

function RedirectIfIsGuest({ role, children }) {
    if (role !== GUEST_ROLE) {
        return <Navigate to="/" replace />;
    }
    return children;
}

function IsUserOwnerOfClient({ isAccess, role, children }) {
    if (!isAccess && role !== SUPERADMIN_ROLE) {
        return <Navigate to="/console/profile" replace />;
    }
    return children;
}

RedirectIfNotAdmin.propTypes = {
    role: PropTypes.any,
    children: PropTypes.any,
};

IsUserOwnerOfClient.propTypes = {
    isAccess: PropTypes.bool,
    role: PropTypes.any,
    children: PropTypes.any,
};
